// @ts-nocheck
import { useCallback, useEffect, useState } from "react";
import { api, type RealBalance } from "@/lib/algo/api";
import { walletLayoutPos, type FlowGraph } from "@/lib/algo/graph";

export type Backend = {
  online: boolean | null;
  balances: Map<string, RealBalance>;
  refresh: () => void;
  createReal: (count: number, pos?: { x: number; y: number }) => Promise<void>;
  importReal: (secretKey: string) => Promise<void>;
  removeReal: (id: string) => Promise<void>;
  syncFromBackend: () => Promise<void>;
};

// Detects the local backend. When offline (e.g. the public deploy), the app
// stays a pure sim. When online, wallets become REAL keypairs with live balances.
export function useBackend(update: (fn: (d: FlowGraph) => void) => void): Backend {
  const [online, setOnline] = useState<boolean | null>(null);
  const [balances, setBalances] = useState<Map<string, RealBalance>>(new Map());

  const refresh = useCallback(() => {
    api.balances()
      .then((r) => setBalances(new Map(r.balances.map((b) => [b.id, b]))))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    let alive = true;
    api.status()
      .then(() => { if (alive) { setOnline(true); refresh(); } })
      .catch(() => { if (alive) setOnline(false); });
    return () => { alive = false; };
  }, [refresh]);

  // auto-refresh balances so deposits/transfers show without manual action
  useEffect(() => {
    if (online !== true) return;
    const t = setInterval(refresh, 8000);
    return () => clearInterval(t);
  }, [online, refresh]);

  const createReal = useCallback(async (count: number, pos?: { x: number; y: number }) => {
    const r = await api.create(count);
    update((d) => {
      r.created.forEach((w, i) => {
        d.wallets.push({
          id: w.id, label: w.label, group: "core", fundingSol: 0, enabled: true,
          publicKey: w.publicKey, ...(pos ?? walletLayoutPos(d.wallets.length + i)),
        });
      });
    });
    refresh();
  }, [update, refresh]);

  const importReal = useCallback(async (secretKey: string) => {
    if (!import.meta.env.DEV) return; // private-key import is dev-only; excluded from the public bundle
    const { apiLive } = await import("@/lib/algo/apiLive");
    const r = await apiLive.importKey(secretKey);
    update((d) => {
      r.created.forEach((w, i) => {
        d.wallets.push({
          id: w.id, label: w.label, group: "core", fundingSol: 0, enabled: true,
          publicKey: w.publicKey, ...walletLayoutPos(d.wallets.length + i),
        });
      });
    });
    refresh();
  }, [update, refresh]);

  const removeReal = useCallback(async (id: string) => {
    await api.remove(id).catch(() => undefined);
    update((d) => {
      d.wallets = d.wallets.filter((w) => w.id !== id);
      d.edges = d.edges.filter((e) => e.source !== id && e.target !== id);
    });
  }, [update]);

  const syncFromBackend = useCallback(async () => {
    const r = await api.list();
    if (!r.wallets || r.wallets.length === 0) return;
    update((d) => {
      d.wallets = r.wallets.map((w, i) => ({
        id: w.id, label: w.label, group: "core", fundingSol: 0, enabled: true,
        publicKey: w.publicKey, ...walletLayoutPos(i),
      }));
      const ids = new Set(d.wallets.map((w) => w.id));
      d.edges = d.edges.filter((e) => (e.source === "launch" || ids.has(e.source)) && ids.has(e.target));
      d.sells = d.sells.filter((s) => ids.has(s.walletId));
    });
    refresh();
  }, [update, refresh]);

  return { online, balances, refresh, createReal, importReal, removeReal, syncFromBackend };
}
